import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import LocationSelector from '../components/LocationSelector';
import IdentitySelector from '../components/IdentitySelector';
import EmailVerification from '../components/EmailVerification';
import TrustScore from '../components/TrustScore';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import { getCollegesForCity, getGraduationYearOptions } from '../data/colleges';
import Navbar from '../components/Navbar';

const INTERESTS = [
  'community_organizing',
  'cultural_events',
  'professional_networking',
  'sports_recreation',
  'arts_music',
  'education_mentorship'
];

function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    age: '',
    profession: '',
    hometownCity: '',
    hometownState: '',
    hometownCountry: '',
    currentCity: '',
    currentState: '',
    currentCountry: '',
    interests: [],
    isStudent: false,
    college: '',
    graduationYear: '',
    major: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        gender: user.gender || '',
        age: user.age || '',
        profession: user.profession || '',
        hometownCity: user.hometown_city || '',
        hometownState: user.hometown_state || '',
        hometownCountry: user.hometown_country || 'US',
        currentCity: user.current_city || '',
        currentState: user.current_state || '',
        currentCountry: user.current_country || 'US',
        interests: user.interests || [],
        isStudent: user.is_student || false,
        college: user.college || '',
        graduationYear: user.graduation_year ? String(user.graduation_year) : '',
        major: user.major || ''
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleInterestToggle = (interest) => {
    const newInterests = formData.interests.includes(interest)
      ? formData.interests.filter(i => i !== interest)
      : [...formData.interests, interest];
    setFormData({ ...formData, interests: newInterests });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };

      // Build profile update payload - only include non-empty values
      const profileUpdate = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        hometownCity: formData.hometownCity,
        hometownState: formData.hometownState,
        hometownCountry: formData.hometownCountry,
        currentCity: formData.currentCity,
        currentState: formData.currentState,
        currentCountry: formData.currentCountry
      };

      // Only include optional fields if they have values
      if (formData.gender) profileUpdate.gender = formData.gender;
      if (formData.age) profileUpdate.age = parseInt(formData.age);
      if (formData.profession) profileUpdate.profession = formData.profession;
      
      // Student information
      profileUpdate.isStudent = formData.isStudent;
      if (formData.isStudent) {
        if (formData.college) profileUpdate.college = formData.college;
        if (formData.graduationYear) profileUpdate.graduationYear = parseInt(formData.graduationYear);
        if (formData.major) profileUpdate.major = formData.major;
      } else {
        // Clear student fields if not a student
        profileUpdate.college = null;
        profileUpdate.graduationYear = null;
        profileUpdate.major = null;
      }

      // Update profile
      await axios.put('/api/users/profile', profileUpdate, { headers });

      // Identities are managed separately via IdentitySelector component

      // Update interests
      await axios.put('/api/users/interests', {
        interests: formData.interests
      }, { headers });

      // Recalculate trust score after profile update
      try {
        await axios.post('/api/trust-score/recalculate', {}, { headers });
      } catch (scoreError) {
        console.log('Trust score recalculation failed:', scoreError);
      }

      setMessage('Profile updated successfully!');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (error) {
      setMessage('Failed to update profile');
      console.error('Error updating profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div>
      <Navbar />

      <div className="container">
        <div className="card">
          <h2>You ✨</h2>
          <p style={{ color: '#666', marginBottom: '8px' }}>
            Help us match you with your people 🤝
          </p>
          <p style={{ color: '#999', fontSize: '14px', marginBottom: '20px' }}>
            * Required fields
          </p>

          {/* Profile Photo Upload */}
          <ProfilePhotoUpload
            currentPhotoUrl={user?.photo_url}
            onPhotoUpdate={(photoUrl) => {
              // Update user context with new photo
              if (user) {
                user.photo_url = photoUrl;
              }
            }}
          />

          <form onSubmit={handleSubmit}>
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Gender *</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non_binary">Non-Binary</option>
                <option value="prefer_not_to_say">Prefer Not to Say</option>
              </select>
            </div>

            <div className="form-group">
              <label>Age (optional)</label>
              <input
                type="number"
                name="age"
                value={formData.age}
                onChange={handleChange}
                min="18"
                max="120"
              />
            </div>

            <div className="form-group">
              <label>What do you do? (optional) 💼</label>
              <input
                type="text"
                name="profession"
                value={formData.profession}
                onChange={handleChange}
                placeholder="e.g., Software Engineer"
              />
            </div>

            {/* Student Status Section */}
            <h3 style={{ marginTop: '32px' }}>Student Info 🎓</h3>
            <div style={{ 
              padding: '16px', 
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #bae6fd',
              marginBottom: '24px'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <strong>Are you a student?</strong>
              </div>
              
              <div style={{ display: 'flex', gap: '24px', marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="isStudent"
                    checked={formData.isStudent === true}
                    onChange={() => setFormData(prev => ({ ...prev, isStudent: true }))}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />
                  <span>Yes, I'm a student</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="isStudent"
                    checked={formData.isStudent === false}
                    onChange={() => setFormData(prev => ({ 
                      ...prev, 
                      isStudent: false,
                      college: '',
                      graduationYear: '',
                      major: ''
                    }))}
                    style={{ marginRight: '8px', cursor: 'pointer' }}
                  />
                  <span>No</span>
                </label>
              </div>

              {/* Show college fields only if student */}
              {formData.isStudent && (
                <div style={{ 
                  marginTop: '16px',
                  paddingTop: '16px',
                  borderTop: '1px solid #bae6fd'
                }}>
                  <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                    Connect with people from your college! 🎓
                  </p>
                  
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label>College/University (optional)</label>
                    {formData.currentCity && getCollegesForCity(formData.currentCity).length > 1 ? (
                      <select
                        name="college"
                        value={formData.college}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select your college</option>
                        {getCollegesForCity(formData.currentCity).map(college => (
                          <option key={college} value={college}>{college}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        name="college"
                        placeholder="e.g., UCLA, MIT, IIT Delhi"
                        value={formData.college}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                      />
                    )}
                    {formData.currentCity && getCollegesForCity(formData.currentCity).length > 1 && (
                      <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                        Showing colleges in {formData.currentCity}
                      </small>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Graduation Year (optional)</label>
                      <select
                        name="graduationYear"
                        value={formData.graduationYear}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                      >
                        <option value="">Select year</option>
                        {getGraduationYearOptions().map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group" style={{ flex: 1 }}>
                      <label>Major (optional)</label>
                      <input
                        type="text"
                        name="major"
                        placeholder="e.g., Computer Science"
                        value={formData.major}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '10px',
                          borderRadius: '4px',
                          border: '1px solid #ddd',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <h3>Location</h3>
            
            <h4>Hometown</h4>
            <LocationSelector
              country={formData.hometownCountry}
              state={formData.hometownState}
              city={formData.hometownCity}
              onCountryChange={(value) => setFormData(prev => ({ ...prev, hometownCountry: value }))}
              onStateChange={(value) => setFormData(prev => ({ ...prev, hometownState: value }))}
              onCityChange={(value) => setFormData(prev => ({ ...prev, hometownCity: value }))}
              required={true}
              prefix="hometown"
            />

            <h4 style={{ marginTop: '24px' }}>Current Location</h4>
            <LocationSelector
              country={formData.currentCountry}
              state={formData.currentState}
              city={formData.currentCity}
              onCountryChange={(value) => setFormData(prev => ({ ...prev, currentCountry: value }))}
              onStateChange={(value) => setFormData(prev => ({ ...prev, currentState: value }))}
              onCityChange={(value) => setFormData(prev => ({ ...prev, currentCity: value }))}
              required={true}
              prefix="current"
            />

          </form>

          {/* Identity Selector - outside form to prevent form submission conflicts */}
          <IdentitySelector
            userId={user?.user_id}
            country={formData.hometownCountry}
            country2={formData.currentCountry}
            onIdentitiesChange={() => {
              console.log('Identities updated');
            }}
          />

          <form onSubmit={handleSubmit}>
            <h3>Interests (optional) 🎨</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px' }}>
              {INTERESTS.map(interest => (
                <label key={interest} style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  backgroundColor: formData.interests.includes(interest) ? '#4CAF50' : 'white',
                  color: formData.interests.includes(interest) ? 'white' : '#333'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.interests.includes(interest)}
                    onChange={() => handleInterestToggle(interest)}
                    style={{ marginRight: '8px' }}
                  />
                  {interest.replace(/_/g, ' ')}
                </label>
              ))}
            </div>

            {message && (
              <div className={message.includes('success') ? 'success' : 'error'}>
                {message}
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving... 💾' : 'Save it ✅'}
            </button>
          </form>

          {/* Email Verification Section */}
          <div style={{ marginTop: '40px', paddingTop: '40px', borderTop: '2px solid #e0e0e0' }}>
            <EmailVerification />
          </div>
          
          {/* Trust Score Section */}
          <div style={{ marginTop: '40px', paddingTop: '40px', borderTop: '2px solid #e0e0e0' }}>
            <TrustScore />
          </div>

          {/* Account Management */}
          <div style={{ marginTop: '40px', paddingTop: '40px', borderTop: '2px solid #e0e0e0' }}>
            <h3 style={{ color: '#666', marginBottom: '16px' }}>🔒 Account</h3>
            <button
              onClick={async () => {
                const confirmed = window.confirm(
                  'Are you sure you want to export all your data? This will download a JSON file with everything we have.'
                );
                if (!confirmed) return;
                try {
                  const token = localStorage.getItem('accessToken');
                  const res = await axios.get('/api/users/me/export', {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'my-belong-data.json';
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (err) {
                  alert('Failed to export data');
                }
              }}
              style={{
                padding: '10px 20px', borderRadius: '8px', border: '1px solid #9b59b6',
                backgroundColor: 'white', color: '#9b59b6', cursor: 'pointer', marginRight: '12px', fontSize: '14px'
              }}
            >
              📥 Export my data
            </button>
            <button
              onClick={async () => {
                const confirmed = window.confirm(
                  '⚠️ This will permanently delete your account and anonymize all your data. This cannot be undone. Are you sure?'
                );
                if (!confirmed) return;
                const doubleConfirm = window.confirm(
                  'Last chance — type OK to confirm you want to delete your account forever.'
                );
                if (!doubleConfirm) return;
                try {
                  const token = localStorage.getItem('accessToken');
                  await axios.delete('/api/users/me', {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  localStorage.clear();
                  navigate('/login');
                } catch (err) {
                  const detail = err.response?.data?.detail || err.response?.data?.error || 'Unknown error';
                  alert(`Failed to delete account: ${detail}`);
                }
              }}
              style={{
                padding: '10px 20px', borderRadius: '8px', border: '1px solid #e74c3c',
                backgroundColor: 'white', color: '#e74c3c', cursor: 'pointer', fontSize: '14px'
              }}
            >
              🗑️ Delete my account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;

